import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { Button, Stack, Typography } from "@mui/material";
import CmsContentConnectContextProvider from "../../../contexts/CmsContentConnectContext";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TranslateDialogRef } from "../../dialog/TranslateDialog";
import { FormValues } from "../types";
import { reindex, reorder } from "../../../utils/cms";
import { FormField } from "../../../api/types/form";
import ModalEntry from "./modal-entry";
import { FormFieldProps } from "./index";
import { useFormErrors } from "../../../contexts/FormContext";
import { PlusCircleOutlined, TranslationOutlined } from "@ant-design/icons";
import { useTranslation } from "../../../contexts/TranslationContext";
import { makeHash } from "../../../utils/static";

export default function AccordionFormField({
                                             type,
                                             name,
                                             label,
                                             value,
                                             required,
                                             disabled,
                                             onChange,
                                             children,
                                             cssClassName,
                                             help,
                                             hasTimePublishing = false,
                                             attr,
                                             inCmsEdit = false,
                                             locale,
                                             formType,
                                             ...props
                                           }: FormFieldProps) {
  const translationRef = useRef<TranslateDialogRef>();

  const {translate} = useTranslation();

  const { error } = useFormErrors(name);
  const [rows, setRows] = useState<Array<FormValues>>(value);


  const sortable = Boolean(attr?.sortable);
  const translatable = Boolean(attr?.translatable);

  useEffect(() => {
    if (makeHash(value) !== makeHash(rows)) {
      onChange(rows);
    }
  }, [rows]);

  const changeRowValue = (index: number, newValues: FormValues) => {
    setRows(draft => {
      const temp = Array.from(draft ?? []);
      temp[index] = newValues;
      return temp;
    });
  };

  const addRow = (values: FormValues = {}) => {
    setRows(draft => {
      const temp = Array.from(draft ?? []);
      temp.push(values);
      return temp;
    });
  };

  const removeRow = (index: number) => {
    setRows(draft => {
      const temp = Array.from(draft);
      temp.splice(index, 1);

      return temp;
    });
  };

  const copyRow = (index: number) => {
    setRows(draft => {
      const temp = Array.from(draft);
      const element = temp.at(index);
      if (element) {
        return [...temp.slice(0, index), element, ...temp.slice(index)];
      }
      return temp;
    });
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) return;
    setRows(draft => {
      const reordered = reorder(draft, source.index, destination.index);
      if (sortable) {
        return reindex(reordered);
      }
      return reordered;
    });
  };

  useEffect(() => {
    if (translatable) {
      const firstRow = rows?.at(0);
      if (firstRow) {
        translationRef.current?.registerUpdateCallback(
          (translationResponse, targetLocale: string) => {
            addRow({
              locale: targetLocale,
              content: translationResponse
            });
          }
        );
        translationRef.current?.setTranslateObj({
          text: firstRow.content,
          sourceLocale: firstRow.locale
        });
      }
    }
  }, [rows, translatable, translationRef.current]);

  const buildTimePublishingFields = (
    rowValues: FormValues
  ): Array<FormField> => {
    return [
      {
        type: "datetime",
        name: "startDate",
        label: "pages.cms.pages.time_publishing.start_date",
        required: true,
        value: rowValues["startDate"]
      },
      {
        type: "datetime",
        name: "endDate",
        label: "pages.cms.pages.time_publishing.end_date",
        required: true,
        value: rowValues["endDate"]
      }
    ];
  };

  const renderModalEntry = (rowValues: FormValues, index: number) => {
    return (
      <ModalEntry
        key={index}
        index={index}
        fieldConfig={{
          type,
          name,
          label,
          required,
          children,
          attr
        }}
        values={rowValues}
        removeRow={removeRow}
        copyRow={copyRow}
        onValueChange={newValues => changeRowValue(index, newValues)}
        errors={error}
        additionalFields={
          hasTimePublishing
            ? buildTimePublishingFields(rowValues)
            : []
        }
        withConnect={inCmsEdit}
        locale={locale}
        connectOptions={{
          name: "partnerConnect",
          label: "pages.cms.pages.partner.connect",
          matcher: {
            image: "KeyVisual",
            logo: "Logo",
            link: "Link"
          }
        }}
      />
    );
  };


  const ListComponent = useMemo(() => {
    if (!sortable) {
      return (
        <Stack
          component="div"
          height="100%"
        >
          {rows?.map(renderModalEntry)}
        </Stack>
      );
    }

    return (
      // @ts-ignore
      <DragDropContext onDragEnd={onDragEnd} enableDefaultSensors>
        {/* @ts-ignore */}
        <Droppable
          droppableId={`modal-entry-sorted-${name}`}
          mode="standard"
          direction="vertical">
          {provided => (
            <Stack
              component="div"
              height="100%"
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ mb: 4 }}>
              {rows?.map(renderModalEntry)}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    );
  }, [name, sortable, rows]);

  return (
    <CmsContentConnectContextProvider name={formType}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flex={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h5">
              {translate(label)}
            </Typography>
            <Button
              startIcon={<PlusCircleOutlined />}
              onClick={() => addRow()}>
              <Typography textTransform="none" lineHeight={1}>
                {translate(label)}
              </Typography>
            </Button>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            {translatable && (
              <Button
                color="info"
                startIcon={<TranslationOutlined />}
                onClick={() =>
                  translationRef.current?.open()
                }>
                <Typography
                  textTransform="none"
                  lineHeight={1}>
                  {translate(
                    'form.general.field_context.translate_val',
                  )}
                </Typography>
              </Button>
            )}
          </Stack>
        </Stack>
        {ListComponent}
      </Stack>
    </CmsContentConnectContextProvider>
  );
}
